import {
	ButtonWithLoading,
	ErrorModal,
	MesfModal,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid, Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { get } from "lodash-es";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useModalStyles } from "../../../../eaf-processes/styles/modalStyles";
import { useEAFDataEntryActions } from "../../../hooks/useEAFDataEntryActions";
import { Probe } from "../../../models/eaf.models";
import { DateTimePickerDE } from "../../form-fields/common/DateTimePickerDE";
import { NumberTextFieldDE } from "../../form-fields/common/NumberTextFieldDE";
import { validateField } from "../../tables/utils/validateMinMax";
type Props = {
	show: boolean;
	onHide: () => void;
	date: Date;
	limitDates?: {
		start: Date;
		end: Date | null;
	};
};
export const CreateProbe = ({ show, onHide, date, limitDates }: Props) => {
	const { state } = useUTLSettingsContext();
	const MIN_TEMP_MANUAL = get(state.settings, "MAX_TEMP_PROBE", 2500);
	const MAX_TEMP_MANUAL = get(state.settings, "MIN_TEMP_PROBE", 3500);

	const MIN_O2_MANUAL = get(state.settings, "MAX_02_PROBE", 0);
	const MAX_O2_MANUAL = get(state.settings, "MIN_02_PROBE", 2000);

	const { saveProbe } = useEAFDataEntryActions();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(false);
	const classes = useModalStyles();
	const {
		getValues,
		handleSubmit,
		setValue,
		control,
		reset,
		formState: { errors },
	} = useForm<Probe>({});
	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};
	const onSubmit: SubmitHandler<any> = async (data: any) => {
		setIsSubmitLoading(true);
		const resp = await saveProbe(data);
		setIsSubmitLoading(false);
		onHide();
	};
	useEffect(() => {
		if (show) {
			reset();
			setValue("Timestamp", date);
			setValue("Temperature", 0);
			setValue("Oxygen", 0);
		}
	}, [show]);
	return (
		<>
			<Grid container>
				<Grid item>
					<MesfModal
						title="NEW MANUAL PROBE"
						open={show}
						handleClose={() => onHide()}
						id="create-probe-modal"
						maxWidth="sm"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										<Controller
											name="Temperature"
											control={control}
											defaultValue={0}
											rules={{
												required: "Temperature is required",
												validate: (value) =>
													validateField(
														value,
														MIN_TEMP_MANUAL,
														MAX_TEMP_MANUAL,
														"Temperature",
													),
											}}
											render={({ field }) => (
												<NumberTextFieldDE
													{...field}
													value={parseInt(getValues("Temperature").toFixed(0))}
													label="Temperature [F]"
													onChange={(val) => setValue("Temperature", val)}
													onBlur={() => {}}
												/>
											)}
										/>
										{errors.Temperature && (
											<span className={classes.errorLabel}>
												{errors.Temperature.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={6}>
										<Controller
											name="Oxygen"
											control={control}
											defaultValue={0}
											rules={{
												validate: (value) =>
													validateField(
														value,
														MIN_O2_MANUAL,
														MAX_O2_MANUAL,
														"Oxygen",
													),
											}}
											render={({ field }) => (
												<NumberTextFieldDE
													{...field}
													value={getValues("Oxygen")}
													label="Oxygen"
													onChange={(val) => setValue("Oxygen", val)}
													onBlur={() => {}}
												/>
											)}
										/>
										{errors.Oxygen && (
											<span className={classes.errorLabel}>
												{errors.Oxygen.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={6}>
										<Controller
											name="Timestamp"
											control={control}
											defaultValue={new Date()}
											rules={{
												required: "Timestamp is required",
												validate: (value) => {
													if (limitDates?.start && value < limitDates.start) {
														return "Timestamp must be between the start and end of the process";
													} else if (
														limitDates?.end &&
														value > limitDates.end
													) {
														return "Timestamp must be less than the end of the process";
													} else {
														return true;
													}
												},
											}}
											render={({ field }) => (
												<DateTimePickerDE
													{...field}
													format={"HH:mm - MM/DD/YYYY"}
													value={getValues("Timestamp")}
													label="Time"
													error={errors.Timestamp?.message}
													onChange={(val) => {
														if (val) {
															setValue("Timestamp", val);
														}
													}}
													onBlur={() => {}}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={() => onHide()}
											size="large"
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<ButtonWithLoading
											fullWidth
											isLoading={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
											size="large"
										>
											Save
										</ButtonWithLoading>
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
					<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
						<MuiAlert
							elevation={6}
							variant="filled"
							severity="success"
							onClose={handleClose}
						>
							The probe was created successfully
						</MuiAlert>
					</Snackbar>
				</Grid>
			</Grid>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Manual Probe"
			/>
		</>
	);
};
